import React from 'react';
import classNames from 'classnames';
import styled from '@emotion/styled';
import { useFocusRing } from '@react-aria/focus';
import { semantic_colors, shapes, text_styles } from '../../foundation';
import { AriaFocusProps } from '../../types/common';
import { IconCheckBold, IconMinusBold } from '../icons/generated';
import { buildCSSWithLength } from '../../styles';

export type CheckboxSize = 'small' | 'medium' | 'large';
export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>, AriaFocusProps {
  checked?: boolean;
  disabled?: boolean;
  indeterminate?: boolean;
  /** @default 'medium' */
  size?: CheckboxSize;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const sizeStyles = {
  small: {
    checkMark: 14,
    icon: 10,
    spacing: 6,
    font: text_styles.Body_12_Regular,
    markMargin: '2px 0',
  },
  medium: {
    checkMark: 18,
    icon: 14,
    spacing: 8,
    font: text_styles.Body_13_Regular,
    markMargin: '1px 0',
  },
  large: {
    checkMark: 22,
    icon: 18,
    spacing: 10,
    font: text_styles.Body_14_Regular,
    markMargin: '0',
  },
};

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    { onChange, size = 'medium', disabled, indeterminate, children, className, unstableFocusVisible, ...props },
    ref,
  ) => {
    const { isFocusVisible, focusProps } = useFocusRing({ within: true });
    const renderToIcon = () => {
      const icon_props = {
        'color': semantic_colors.content.primary_inverse,
        'size': sizeStyles[size].icon,
        'focusable': false,
        'aria-hidden': true,
        'data-role': 'check-icon',
      };
      return indeterminate ? <IconMinusBold {...icon_props} /> : <IconCheckBold {...icon_props} />;
    };

    return (
      <StyledInput
        disabled={disabled}
        className={classNames('pds-checkbox', className)}
        focused={unstableFocusVisible ?? isFocusVisible}
        {...focusProps}
      >
        <input
          ref={ref}
          type='checkbox'
          data-indeterminate={indeterminate}
          disabled={disabled}
          {...props}
          onChange={onChange}
        />
        <Checkmark disabled={disabled} indeterminate={indeterminate} size={size} data-role='check-mark'>
          {renderToIcon()}
        </Checkmark>
        {children && (
          <Text size={size} disabled={disabled}>
            {children}
          </Text>
        )}
      </StyledInput>
    );
  },
);

const StyledInput = styled.label<{ disabled?: boolean; focused?: boolean }>`
  display: flex;
  align-items: center;
  position: relative;
  min-width: 18px;
  min-height: 20px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  user-select: none;
  transition: all 0.1s linear;

  input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
    &:focus {
      outline: none;
      box-shadow: none;
    }
  }

  ${({ focused }) => focused && `outline: 3px solid ${semantic_colors.tab_focus};`}

  &:hover:not(:has(label:hover, [data-role='check-mark']:hover)) > [data-role='check-mark'],
  [data-role='check-mark']:hover {
    border: ${({ disabled }) =>
      `solid 1px ${disabled ? semantic_colors.border.primary : semantic_colors.accent.primary}`};
  }

  [data-role='check-mark'] {
    border: ${({ disabled }) =>
      `solid 1px ${disabled ? semantic_colors.border.primary : semantic_colors.border.secondary}`};
  }

  input:checked ~ [data-role='check-mark'],
  input[data-indeterminate='true'] ~ [data-role='check-mark'] {
    ${({ disabled }) =>
      disabled
        ? `
        background-color: ${semantic_colors.accent.disabled};
        border: solid 1px ${semantic_colors.accent.disabled};
    `
        : `
        background-color: ${semantic_colors.accent.primary};
        border: solid 1px ${semantic_colors.accent.primary};
    `}
    [data-role='check-icon'] {
      display: block;
    }
  }
`;

const Checkmark = styled.span<{ disabled?: boolean; indeterminate?: boolean; size: CheckboxSize }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: ${({ disabled }) =>
    disabled ? `solid 1px ${semantic_colors.border.primary}` : `solid 1px ${semantic_colors.border.secondary}`};
  background-color: ${({ disabled }) =>
    disabled ? semantic_colors.fill.disabled : semantic_colors.background.surface};
  transition: all 0.1s linear;
  ${({ size }) => `
    ${buildCSSWithLength('width', sizeStyles[size].checkMark)};
    ${buildCSSWithLength('height', sizeStyles[size].checkMark)};
    margin: ${sizeStyles[size].markMargin};
  `};
  ${shapes.border_radius.xsmall};
  [data-role='check-icon'] {
    display: none;
  }

  ${({ indeterminate, disabled }) =>
    indeterminate &&
    `
      background-color: ${disabled ? semantic_colors.accent.disabled : semantic_colors.accent.primary};
      border: solid 1px ${disabled ? semantic_colors.accent.disabled : semantic_colors.accent.primary};
      [data-role='check-icon'] {
        display: block;
      }
    `}
`;

const Text = styled.span<{ disabled?: boolean; size: CheckboxSize }>`
  ${({ size }) => buildCSSWithLength('margin-left', sizeStyles[size].spacing)};
  ${({ size }) => sizeStyles[size].font};
  color: ${({ disabled }) => (disabled ? semantic_colors.content.disabled : semantic_colors.content.primary)};
`;
